import { Controller } from "@hotwired/stimulus"
import { announcePopoverOpen, bindExclusivePopover, isPopoverOpen } from "../utils/exclusive-popover.util"
import { installFilterHotkey } from "../utils/filter-hotkey.util"
import { isCompactViewport } from "../utils/pointer.util"
import { cancelPopoverClose, closeWithPopoverMotion } from "../utils/popover-motion.util"

const ACTIVE_FILTER_QUERY_KEYS = [
  "status[]",
  "paymentStatus[]",
  "documentType[]",
  "generatorDocumentType[]",
  "frequency[]",
  "emailDelivery[]",
  "customer",
  "project",
  "rent",
  "paidFrom",
  "paidTo",
  "issuedFrom",
  "issuedTo",
  "dueDateFrom",
  "dueDateTo",
  "totalAmountFrom",
  "totalAmountTo",
  "deliveryDateFrom",
  "deliveryDateTo",
  "receivablesCollectedLast30",
  "hasAmountDue",
  "overdue"
]

// Connects to data-controller="invoices-filter"
export default class extends Controller {
  static targets = [
    "activeFilterIndicator",
    "activeStatusState",
    "activeStatusDocumentType",
    "filterButton",
    "filterMenu",
    "filterItem",
    "filterContent",
    "searchClearButton",
    "searchInput",
    "activeStatusTotalAmount",
    "activeStatusCustomer",
    "activeStatusProject",
    "activeStatusRent",
    "activeStatusExposed",
    "activeStatusDueDate",
    "activeStatusPaymentDate",
    "activeStatusDeliveryDate",
    "activeStatusRecurringDocumentType",
    "activeStatusFrequency",
    "activeStatusEmailDelivery"
  ]

  connect() {
    this.#initializeActiveStatus()
    this.refreshActiveFilterIndicator()
    this.#restoreSearchFromUrl()
    this.boundHandleGlobalKeydown = this.#handleGlobalKeydown.bind(this)
    this.boundHandleGlobalPointerDown = this.#handleGlobalPointerDown.bind(this)
    this.boundHandleResize = this.#handleResize.bind(this)
    this.boundRefreshActiveFilterIndicator = this.refreshActiveFilterIndicator.bind(this)
    this.unbindExclusivePopover = bindExclusivePopover(this.element, () => this.closeFilterMenu())
    // One mechanism for "F", shared with the five other filter menus. This
    // controller used to carry its own copy, gated on `enableHotkey` AND a
    // `pathname.endsWith("/invoices")` check, so the <kbd>F</kbd> the partial
    // renders did nothing on proformas, quotes, orders and credit notes.
    this.removeFilterHotkey = installFilterHotkey(this)
    window.addEventListener("keydown", this.boundHandleGlobalKeydown, true)
    document.addEventListener("pointerdown", this.boundHandleGlobalPointerDown, true)
    window.addEventListener("resize", this.boundHandleResize, { passive: true })
    this.filterMenuTarget.addEventListener("change", this.boundRefreshActiveFilterIndicator)
    this.filterMenuTarget.addEventListener("input", this.boundRefreshActiveFilterIndicator)
  }

  disconnect() {
    window.removeEventListener("keydown", this.boundHandleGlobalKeydown, true)
    this.removeFilterHotkey?.()
    document.removeEventListener("pointerdown", this.boundHandleGlobalPointerDown, true)
    window.removeEventListener("resize", this.boundHandleResize)
    this.filterMenuTarget.removeEventListener("change", this.boundRefreshActiveFilterIndicator)
    this.filterMenuTarget.removeEventListener("input", this.boundRefreshActiveFilterIndicator)
    this.unbindExclusivePopover?.()
  }

  #initializeActiveStatus() {
    const urlSearchParams = new URLSearchParams(window.location.search)
    if (this.hasActiveStatusStateTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["status[]", "paymentStatus[]", "overdue"]),
        this.activeStatusStateTarget
      )
    }
    if (this.hasActiveStatusCustomerTarget) {
      this.toggleActiveStatus(this.#toggleChecker(urlSearchParams, ["customer"]), this.activeStatusCustomerTarget)
    }
    if (this.hasActiveStatusProjectTarget) {
      this.toggleActiveStatus(this.#toggleChecker(urlSearchParams, ["project"]), this.activeStatusProjectTarget)
    }
    if (this.hasActiveStatusRentTarget) {
      this.toggleActiveStatus(this.#toggleChecker(urlSearchParams, ["rent"]), this.activeStatusRentTarget)
    }
    if (this.hasActiveStatusTotalAmountTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["totalAmountFrom", "totalAmountTo"]),
        this.activeStatusTotalAmountTarget
      )
    }
    if (this.hasActiveStatusExposedTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["issuedFrom", "issuedTo"]),
        this.activeStatusExposedTarget
      )
    }
    if (this.hasActiveStatusDueDateTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["dueDateFrom", "dueDateTo"]),
        this.activeStatusDueDateTarget
      )
    }
    if (this.hasActiveStatusPaymentDateTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["paidFrom", "paidTo"]),
        this.activeStatusPaymentDateTarget
      )
    }
    if (this.hasActiveStatusDeliveryDateTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["deliveryDateFrom", "deliveryDateTo"]),
        this.activeStatusDeliveryDateTarget
      )
    }
    if (this.hasActiveStatusDocumentTypeTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["documentType[]"]),
        this.activeStatusDocumentTypeTarget
      )
    }
    if (this.hasActiveStatusRecurringDocumentTypeTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["generatorDocumentType[]"]),
        this.activeStatusRecurringDocumentTypeTarget
      )
    }
    if (this.hasActiveStatusFrequencyTarget) {
      this.toggleActiveStatus(this.#toggleChecker(urlSearchParams, ["frequency[]"]), this.activeStatusFrequencyTarget)
    }
    if (this.hasActiveStatusEmailDeliveryTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["emailDelivery[]"]),
        this.activeStatusEmailDeliveryTarget
      )
    }
  }

  #toggleChecker(urlSearchParams, keys) {
    if (urlSearchParams.has("quickFilter")) {
      return false
    }
    return keys.some((key) => urlSearchParams.has(key))
  }

  toggleActiveStatus(isActive, target) {
    if (isActive) {
      target.classList.remove("d-none")
    } else {
      target.classList.add("d-none")
    }
  }

  refreshActiveFilterIndicator() {
    if (!this.hasActiveFilterIndicatorTarget) return

    const isActive = this.#hasActiveFilterQueryParams() || this.#hasActiveFilterControls()
    this.activeFilterIndicatorTarget.classList.toggle("d-none", !isActive)
    this.filterButtonTarget.classList.toggle("filter-button--has-active-filters", isActive)
  }

  openFilterMenu(event) {
    event?.preventDefault()
    if (this.#isPopoverOpen()) return
    announcePopoverOpen(this.element)
    this.#applyPopoverViewportSizing()
    cancelPopoverClose(this.filterMenuTarget)
    this.filterMenuTarget.showPopover()
    this.filterButtonTarget.classList.add("filter-button--active")
  }

  closeFilterMenu() {
    if (!this.#isPopoverOpen()) return
    closeWithPopoverMotion(this.filterMenuTarget, () => {
      this.filterMenuTarget.hidePopover()
    })
    this.filterButtonTarget.classList.remove("filter-button--active")
  }

  selectFilter(event) {
    const { currentTarget } = event
    const selectedId = currentTarget.dataset.id

    this.filterItemTargets.forEach((el) => {
      el.classList.toggle("sidebar__tab--active", el === currentTarget)
    })

    this.filterContentTargets.forEach((el) => {
      el.classList.toggle("tab-content--hidden", el.id !== selectedId)
    })
  }

  clearSearch(event) {
    event?.preventDefault()
    if (!this.hasSearchInputTarget) return

    this.searchInputTarget.value = ""
    this.searchInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    // The button that was just activated hides itself the moment the field
    // empties (the CSS keys it on `:placeholder-shown`), so focus would fall to
    // <body> and the next Tab would restart at the top of the document. The
    // field is still on screen at every width now, so focus always goes back to
    // it — there is no longer a collapsing row for focus to have nowhere to go.
    this.searchInputTarget.focus()
  }

  handleSearchKeydown(event) {
    if (event.key !== "Escape" || !this.#hasSearchValue()) return

    event.preventDefault()
    this.clearSearch(event)
  }

  #handleGlobalKeydown(event) {
    // Synthetic non-KeyboardEvents dispatched by browser extensions carry no `key`
    // and read `undefined` for every modifier. A real keypress always carries a
    // string `key`, so requiring one rejects them outright. LUC-713.
    if (typeof event.key !== "string") return

    if (event.key === "Escape" && this.#isPopoverOpen()) {
      event.preventDefault()
      this.closeFilterMenu()
      return
    }
  }

  #handleGlobalPointerDown(event) {
    if (!this.#isPopoverOpen()) return
    if (this.filterMenuTarget.contains(event.target)) return
    if (this.filterButtonTarget.contains(event.target)) return
    this.closeFilterMenu()
  }

  #isPopoverOpen() {
    return isPopoverOpen(this.filterMenuTarget)
  }

  #handleResize() {
    if (!this.#isPopoverOpen()) return
    this.#applyPopoverViewportSizing()
  }

  #applyPopoverViewportSizing() {
    if (!this.hasFilterMenuTarget || !this.hasFilterButtonTarget) return

    const buttonRect = this.filterButtonTarget.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const spacing = 16
    const isMobile = isCompactViewport()

    if (isMobile) {
      const mobileInset = 8
      const top = Math.max(mobileInset, Math.floor(buttonRect.bottom + mobileInset))
      const maxHeight = Math.max(320, viewportHeight - top - mobileInset)
      this.filterMenuTarget.style.position = "fixed"
      this.filterMenuTarget.style.top = `${top}px`
      this.filterMenuTarget.style.left = `${mobileInset}px`
      this.filterMenuTarget.style.right = `${mobileInset}px`
      this.filterMenuTarget.style.bottom = "auto"
      this.filterMenuTarget.style.width = "auto"
      this.filterMenuTarget.style.maxHeight = `${maxHeight}px`
      this.filterMenuTarget.style.zIndex = "1004"
      this.filterMenuTarget.style.setProperty("--filter-menu-inset", `${mobileInset}px`)
      this.filterMenuTarget.style.setProperty("--filter-menu-max-height", `${maxHeight}px`)
      this.filterMenuTarget.dataset.mobileSheet = "true"
      return
    }

    const availableBelow = Math.floor(viewportHeight - buttonRect.bottom - spacing)
    const maxHeight = Math.max(320, availableBelow)
    this.#clearMobilePopoverPosition()
    this.filterMenuTarget.style.removeProperty("--filter-menu-inset")
    delete this.filterMenuTarget.dataset.mobileSheet
    this.filterMenuTarget.style.setProperty("--filter-menu-max-height", `${maxHeight}px`)
  }

  #clearMobilePopoverPosition() {
    this.filterMenuTarget.style.removeProperty("position")
    this.filterMenuTarget.style.removeProperty("top")
    this.filterMenuTarget.style.removeProperty("left")
    this.filterMenuTarget.style.removeProperty("right")
    this.filterMenuTarget.style.removeProperty("bottom")
    this.filterMenuTarget.style.removeProperty("width")
    this.filterMenuTarget.style.removeProperty("z-index")
  }

  #hasActiveFilterQueryParams() {
    const urlSearchParams = new URLSearchParams(window.location.search)
    if (urlSearchParams.has("quickFilter")) return false

    return ACTIVE_FILTER_QUERY_KEYS.some((key) => {
      return urlSearchParams.getAll(key).some((value) => value.trim() !== "")
    })
  }

  #hasActiveFilterControls() {
    if (!this.hasFilterMenuTarget) return false

    return Array.from(this.filterMenuTarget.querySelectorAll("input, select, textarea")).some((element) => {
      if (element.name === "search" || !element.name) return false
      if (element.type === "checkbox" || element.type === "radio") return element.checked

      return element.value.trim() !== ""
    })
  }

  // The field is rendered with a nil value and marked data-turbo-permanent, so
  // on a full page load of a search URL the markup carries no value at all and
  // there is no old node for Turbo to carry one over from. Put it back from the
  // URL, which is where a shared or reloaded search link keeps it.
  #restoreSearchFromUrl() {
    if (!this.hasSearchInputTarget || this.searchInputTarget.value) return

    this.searchInputTarget.value = new URLSearchParams(window.location.search).get("search") || ""
  }

  #hasSearchValue() {
    return this.hasSearchInputTarget && this.searchInputTarget.value.trim() !== ""
  }
}
